<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';

import {
    AppPublishStatusEnum,
    type PublishVersionCommandModel,
    type PublishWithNoteModalData
} from '../../App/Publish/Scripts/publish-index-model';
import StHelpVideoModal from '../Modals/HelpVideo/Scripts/StHelpVideoModal.vue';
import StAppPublishModal from './Components/AppPublishModal/StAppPublishModal.vue';
import { ValidationHelper } from './Helpers/validation-helper';
import { PublishRepository } from './Repositories/publish-repository';
import { type NavbarSettings, SitesRepository } from './Repositories/site-repository';
import type { AppStudioPageProps } from './Vue3/app-studio-page-props';
import { appStudioModalService } from './Components/Modal/app-studio-modal';

interface NavbarModel {
    isAuthenticated: boolean;
}

interface NavbarOptions {
    expressionValidationChecklistEnabled: boolean;
}

type NavbarPageProps = AppStudioPageProps<NavbarModel, NavbarOptions>;

defineOptions({ name: 'navbar-vue-component' });
const props = defineProps<{ pageProps: NavbarPageProps }>();

const headerIconUrl = '/images/HeaderIcon.svg';
const publishVersionRequested = ref(false);
const navbarSettings = ref<NavbarSettings>({} as NavbarSettings);
const helpASInBriefModalComponentRef = ref<InstanceType<typeof StHelpVideoModal> | null>(null);
const pageHelpUrl = ref<string | null>(null);
const isHelpMenuOpen = ref(false);

let pageHelpObserver: MutationObserver | undefined;

const appIdentifier = computed(() => window.softools.appIdentifier);
const hasPublishButton = computed(
    () =>
        !publishVersionRequested.value &&
        !!navbarSettings.value.appPublishStatusInfoResult &&
        navbarSettings.value.appPublishStatusInfoResult.hasAppsEditAccess === true &&
        navbarSettings.value.appPublishStatusInfoResult.appPublishStatusInfo !== undefined &&
        navbarSettings.value.appPublishStatusInfoResult.appPublishStatusInfo.hasUnpublishedChanges === true &&
        navbarSettings.value.appPublishStatusInfoResult.appPublishStatusInfo.latestAvailableVersion !== undefined &&
        navbarSettings.value.appPublishStatusInfoResult.appPublishStatusInfo.appPublishStatus !== AppPublishStatusEnum.InProgress
);
const sitesWorkspaceBaseUrl = computed(() => navbarSettings.value?.sitesWorkspaceBaseUrl ?? '#');
const maximumSnapshotsCountExceeded = computed(
    () => navbarSettings.value?.appPublishStatusInfoResult?.appPublishStatusInfo?.maximumSnapshotsCountExceeded ?? false
);
const appHasExpressionErrors = computed(
    () =>
        props.pageProps.options.expressionValidationChecklistEnabled &&
        (navbarSettings.value?.appPublishStatusInfoResult?.appPublishStatusInfo?.appHasExpressionErrors ?? false)
);
const fieldExpressionErrorUrl = computed(() => `/App/${appIdentifier.value}/Fields/ExpressionErrorsIndex`);

async function loadNavbarSettingsAsync(): Promise<void> {
    try {
        navbarSettings.value = await SitesRepository.getNavbarSettings(appIdentifier.value);
    } catch (error) {
        console.warn('Unable to load navbar settings.', error);
    }
}

function onShowPublishWithNoteModal(): void {
    appStudioModalService.show('publishWithNoteModal');
}

async function onPublishVersionSubmit(publishWithNoteModalData: PublishWithNoteModalData): Promise<void> {
    publishVersionRequested.value = true;

    const publishVersionCommand: PublishVersionCommandModel = {
        targetAppVersion: navbarSettings.value.appPublishStatusInfoResult?.appPublishStatusInfo?.latestAvailableVersion ?? -1,
        notes: publishWithNoteModalData?.notes,
        takeASnapshot: publishWithNoteModalData?.takeASnapshot ?? false
    };

    const result = await PublishRepository.publishVersion(appIdentifier.value, publishVersionCommand);
    ValidationHelper.handleRedirectOrValidatedResult(result);

    if (result.hasErrors) {
        publishVersionRequested.value = false;
    }
}

function showASInBriefHelpModal(): void {
    helpASInBriefModalComponentRef.value?.onShowHelpModal();
}

function syncPageHelpUrl(): void {
    if (typeof document === 'undefined') return;

    const helpLink = document.querySelector<HTMLAnchorElement>(
        '.appstudio-support-icon-link a[href], main a.appstudio-support-link[href], main .appstudio-page-heading > a[href][aria-label$=" support"]'
    );
    pageHelpUrl.value = helpLink?.href ?? null;

    if (!pageHelpUrl.value) isHelpMenuOpen.value = false;
}

function toggleHelpMenu(): void {
    isHelpMenuOpen.value = !isHelpMenuOpen.value;
}

function closeHelpMenu(event: Event): void {
    const target = event.target;
    if (target instanceof Element && target.closest('[data-appstudio-help-menu]')) return;
    isHelpMenuOpen.value = false;
}

function closeHelpMenuOnEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape') isHelpMenuOpen.value = false;
}

onMounted(() => {
    syncPageHelpUrl();
    pageHelpObserver = new MutationObserver(syncPageHelpUrl);
    pageHelpObserver.observe(document.querySelector('main') ?? document.body, {
        attributeFilter: ['href'],
        attributes: true,
        childList: true,
        subtree: true
    });
    document.addEventListener('click', closeHelpMenu);
    document.addEventListener('keydown', closeHelpMenuOnEscape);

    window.addEventListener('reloadapppublishstatusinfo', loadNavbarSettingsAsync);
    void loadNavbarSettingsAsync();
});

onUnmounted(() => {
    pageHelpObserver?.disconnect();
    document.removeEventListener('click', closeHelpMenu);
    document.removeEventListener('keydown', closeHelpMenuOnEscape);
    window.removeEventListener('reloadapppublishstatusinfo', loadNavbarSettingsAsync);
});
</script>

<template>
    <nav class="navbar navbar-expand-lg navbar-dark bg-dark fixed-top appstudio-navbar">
        <div class="appstudio-navbar-left">
            <button id="sidebar-toggle" class="navbar-toggler custom-remove-border" type="button">
                <span class="navbar-toggler-icon"></span>
            </button>

            <div id="h-menu" class="text-white d-none d-lg-block pointer disable-user-select"><i class="fal fa-bars fa-xl"></i></div>

            <ul class="nav navbar-nav appstudio-navbar-home">
                <li class="nav-item">
                    <a class="nav-link" :href="pageProps.urls.home"><i class="fal fa-home-alt fa-xl" aria-hidden="true"></i></a>
                </li>
            </ul>
        </div>

        <a class="navbar-brand appstudio-navbar-brand" :href="pageProps.urls.home">
            <img :src="headerIconUrl" height="30" class="d-inline-block align-top" alt="Softools" />
        </a>

        <button
            class="navbar-toggler custom-remove-border appstudio-navbar-settings-toggle"
            type="button"
            data-toggle="collapse"
            data-target="#navbarColor01"
            aria-controls="navbarColor01"
            aria-expanded="false"
            aria-label="Toggle navigation"
        >
            <span class="fal fa-cog fa-xl"></span>
        </button>

        <div id="navbarColor01" class="collapse navbar-collapse appstudio-navbar-actions">
            <ul class="nav navbar-nav appstudio-navbar-action-list">
                <li v-if="hasPublishButton" id="publish-newversion-navbar-badge" class="nav-item">
                    <a class="nav-link" title="Publish" href="#" @click.prevent="onShowPublishWithNoteModal">
                        <span class="badge rounded-pill badge-green">
                            <svg class="appstudio-navbar-publish-icon">
                                <use xlink:href="#cloud-icon"></use>
                            </svg>
                            Publish
                        </span>
                    </a>
                </li>

                <li class="nav-item">
                    <a class="nav-link" title="Workspace" :href="sitesWorkspaceBaseUrl" target="_blank">
                        <span class="d-lg-none d-xl-none">Workspace</span>
                        <i class="fal fa-bring-forward fa-xl appstudio-navbar-action-icon" aria-hidden="true"></i>
                    </a>
                </li>

                <li v-if="pageHelpUrl" class="nav-item dropdown appstudio-navbar-help" data-appstudio-help-menu>
                    <button
                        class="nav-link appstudio-navbar-help-trigger"
                        type="button"
                        title="Help"
                        aria-haspopup="menu"
                        :aria-expanded="isHelpMenuOpen"
                        @click.stop="toggleHelpMenu"
                    >
                        <span class="d-lg-none d-xl-none">Help</span>
                        <i class="fal fa-question-circle fa-xl appstudio-navbar-action-icon" aria-hidden="true"></i>
                    </button>
                    <div v-if="isHelpMenuOpen" class="dropdown-menu dropdown-menu-end show appstudio-navbar-help-menu" role="menu">
                        <a class="dropdown-item" :href="pageHelpUrl" target="_blank" rel="noopener noreferrer" role="menuitem">
                            <i class="fal fa-file-alt" aria-hidden="true"></i>
                            <span>Page help</span>
                        </a>
                        <a class="dropdown-item" :href="pageProps.urls.support" target="_blank" rel="noopener noreferrer" role="menuitem">
                            <i class="fal fa-life-ring" aria-hidden="true"></i>
                            <span>Support centre</span>
                        </a>
                    </div>
                </li>

                <li v-else class="nav-item">
                    <a class="nav-link" title="Support" :href="pageProps.urls.support" target="_blank">
                        <span class="d-lg-none d-xl-none">Support</span>
                        <i class="fal fa-question-circle fa-xl appstudio-navbar-action-icon" aria-hidden="true"></i>
                    </a>
                </li>

                <li class="nav-item">
                    <a class="nav-link" title="Help - Softools AppStudio in Brief Video" href="#" @click.prevent="showASInBriefHelpModal">
                        <span class="d-lg-none d-xl-none">Help Video</span>
                        <i class="fal fa-video fa-xl appstudio-navbar-action-icon" aria-hidden="true"></i>
                    </a>
                </li>

                <li v-if="pageProps.model.isAuthenticated" class="nav-item">
                    <a class="nav-link" title="Logout" :href="pageProps.urls.logout">
                        <span class="d-lg-none d-xl-none">Logout</span>
                        <i class="fal fa-sign-out-alt fa-xl appstudio-navbar-action-icon" aria-hidden="true"></i>
                    </a>
                </li>
                <li v-else class="nav-item">
                    <span class="d-lg-none d-xl-none">Login</span>
                    <a title="Login" :href="pageProps.urls.login" class="nav-link">Login</a>
                </li>
            </ul>
        </div>

        <StAppPublishModal
            :maximum-snapshots-count-exceeded="maximumSnapshotsCountExceeded"
            :app-has-expression-errors="appHasExpressionErrors"
            :expression-errors-url="fieldExpressionErrorUrl"
            :note-support-text="pageProps.supportText.publishNote"
            :snapshot-support-text="pageProps.supportText.snapshot"
            @on-publish-version-submit="onPublishVersionSubmit"
        />

        <StHelpVideoModal
            ref="helpASInBriefModalComponentRef"
            ref-id="helpASInBriefModalComponentRef"
            :title="pageProps.translations.helpVideoTitle"
            :vimeo-video-url="pageProps.urls.helpVideo"
        />
    </nav>
</template>
